import { useCallback, useEffect, useRef, useState } from 'react';
import { toast } from 'sonner';
import { api, watchSiteChanges } from '../lib/api';
import { endSession } from '../lib/authClient';
import { markSession, invalidateSession, sessionGeneration, currentSessionId, removeLegacySession } from '../lib/sessionState';
import { useIdleSession } from './useIdleSession';

export const useAuthSession = () => {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);
  const [session, setSession] = useState(null);
  const channel = useRef(null);
  const accept = useCallback(data => {
    markSession(data.sessionId);
    setSession({ id: data.sessionId, expires: data.idleExpiresAt });
    setUser(data.user || data); setLoading(false);
    return data.user || data;
  }, []);
  const clear = useCallback(() => { invalidateSession(); setUser(null); setSession(null); setLoading(false); }, []);
  const refresh = useCallback(async () => {
    const version = sessionGeneration();
    try {
      const { data } = await api.get('/auth/me');
      if (version === sessionGeneration()) accept(data);
    } catch (error) {
      if (version !== sessionGeneration()) return;
      if ([401, 403].includes(error.response?.status)) clear();
      else console.warn('Session could not be checked.');
    } finally { if (version === sessionGeneration()) setLoading(false); }
  }, [accept, clear]);
  const expire = useCallback(reason => {
    if (!currentSessionId()) return;
    if (reason === 'idle') {
      endSession(true).then(() => channel.current?.postMessage('refresh')).catch(() => console.warn('Idle sign-out could not reach the server; server inactivity checks still apply.'));
    } else channel.current?.postMessage('refresh');
    clear();
    toast.info(reason === 'idle' ? 'Signed out after 10 minutes of inactivity.' : 'Your session expired. Please sign in again.');
  }, [clear]);
  useIdleSession(session?.id, session?.expires, expire);
  useEffect(() => {
    removeLegacySession();
    if (window.location.hash.includes('session_id=')) { setLoading(false); return; }
    refresh();
  }, [refresh]);
  useEffect(() => {
    const expired = event => expire(event.detail?.includes('inactivity') ? 'idle' : 'expired');
    channel.current = typeof BroadcastChannel === 'undefined' ? null : new BroadcastChannel('maldhowr-auth');
    if (channel.current) channel.current.onmessage = () => { invalidateSession(); refresh(); };
    window.addEventListener('maldhowr-session-expired', expired);
    const stop = watchSiteChanges(refresh);
    const visible = () => { if (!document.hidden) refresh(); };
    document.addEventListener('visibilitychange', visible);
    const timer = setInterval(visible, 15000);
    return () => { channel.current?.close(); clearInterval(timer); stop(); window.removeEventListener('maldhowr-session-expired', expired); document.removeEventListener('visibilitychange', visible); };
  }, [expire, refresh]);
  const finish = useCallback(data => { const principal = accept(data); channel.current?.postMessage('refresh'); return principal; }, [accept]);
  const logout = useCallback(async () => {
    const pending = endSession();
    clear();
    try { await pending; channel.current?.postMessage('refresh'); }
    catch (error) { toast.error('Sign-out could not be completed. Please try again.'); await refresh(); }
  }, [clear, refresh]);
  return { user, loading, setUser, finish, logout };
};